/**
 * Theme Mode Type
 * Represents the user's theme preference
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * LMS Preferences for video player and learning experience
 */
export interface LMSPreferences {
  playback_speed?: number;        // Default playback speed: 0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0
  autoplay_next_lesson?: boolean; // Auto-play next lesson after video ends
  auto_resume_videos?: boolean;   // Resume videos automatically without prompt
}

/**
 * Activation Checklist for onboarding
 */
export interface ActivationChecklist {
  created_first_cycle?: boolean;
  created_first_level?: boolean;
  created_first_subject?: boolean;
  assigned_first_teacher?: boolean;
  explored_first_subject?: boolean;
  created_first_lesson?: boolean;
  created_first_evaluation?: boolean;
  reviewed_schedule?: boolean;
  enrolled_in_first_subject?: boolean;
  viewed_first_lesson?: boolean;
  completed_first_evaluation?: boolean;
  reviewed_progress?: boolean;
}

/**
 * User Preferences Props Interface
 */
export interface UserPreferencesProps {
  theme: ThemeMode;
  onboarding_completed?: boolean;
  onboarding_tour_completed?: boolean;
  onboarding_skipped?: boolean;
  onboarding_completed_at?: string; // ISO timestamp
  tooltips_seen?: string[]; // Array of tooltip IDs
  activation_checklist?: ActivationChecklist;
  checklist_minimized?: boolean;
  checklist_hidden?: boolean;
  lms?: LMSPreferences; // LMS-specific preferences
}

/**
 * User Preferences Value Object
 * Immutable type representing user preference settings
 */
export class UserPreferences {
  private readonly props: UserPreferencesProps;

  private constructor(props: UserPreferencesProps) {
    this.props = props;
  }

  /**
   * Create a new UserPreferences instance
   */
  static create(props: Partial<UserPreferencesProps> = {}): UserPreferences {
    return new UserPreferences({
      theme: props.theme ?? 'light',
      onboarding_completed: props.onboarding_completed ?? false,
      onboarding_tour_completed: props.onboarding_tour_completed ?? false,
      onboarding_skipped: props.onboarding_skipped ?? false,
      onboarding_completed_at: props.onboarding_completed_at,
      tooltips_seen: props.tooltips_seen ?? [],
      activation_checklist: props.activation_checklist ?? {},
      checklist_minimized: props.checklist_minimized ?? false,
      checklist_hidden: props.checklist_hidden ?? false,
      lms: {
        playback_speed: props.lms?.playback_speed ?? 1.0,
        autoplay_next_lesson: props.lms?.autoplay_next_lesson ?? true,
        auto_resume_videos: props.lms?.auto_resume_videos ?? false,
      },
    });
  }

  /**
   * Create UserPreferences from a JSON object (e.g., from database)
   */
  static fromJSON(json: Record<string, unknown> | null | undefined): UserPreferences {
    if (!json) return UserPreferences.create();
    return UserPreferences.create({
      theme: (json.theme as ThemeMode) ?? 'light',
      onboarding_completed: json.onboarding_completed as boolean | undefined,
      onboarding_tour_completed: json.onboarding_tour_completed as boolean | undefined,
      onboarding_skipped: json.onboarding_skipped as boolean | undefined,
      onboarding_completed_at: json.onboarding_completed_at as string | undefined,
      tooltips_seen: json.tooltips_seen as string[] | undefined,
      activation_checklist: json.activation_checklist as ActivationChecklist | undefined,
      checklist_minimized: json.checklist_minimized as boolean | undefined,
      checklist_hidden: json.checklist_hidden as boolean | undefined,
      lms: json.lms as LMSPreferences | undefined,
    });
  }

  /**
   * Get the theme preference
   */
  get theme(): ThemeMode {
    return this.props.theme;
  }

  /**
   * Get onboarding completion status
   */
  get onboardingCompleted(): boolean {
    return this.props.onboarding_completed ?? false;
  }

  /**
   * Get tour completion status
   */
  get tourCompleted(): boolean {
    return this.props.onboarding_tour_completed ?? false;
  }

  /**
   * Get skipped status
   */
  get skipped(): boolean {
    return this.props.onboarding_skipped ?? false;
  }

  /**
   * Get tooltips seen
   */
  get tooltipsSeen(): string[] {
    return this.props.tooltips_seen ?? [];
  }

  /**
   * Get activation checklist
   */
  get activationChecklist(): ActivationChecklist {
    return this.props.activation_checklist ?? {};
  }

  /**
   * Get LMS preferences
   */
  get lmsPreferences(): LMSPreferences {
    return this.props.lms ?? {
      playback_speed: 1.0,
      autoplay_next_lesson: true,
      auto_resume_videos: false,
    };
  }

  /**
   * Create a new instance with updated theme
   */
  withTheme(theme: ThemeMode): UserPreferences {
    return new UserPreferences({ ...this.props, theme });
  }

  /**
   * Create a new instance with updated onboarding status
   */
  withOnboardingStatus(completed: boolean, tourCompleted: boolean, skipped: boolean): UserPreferences {
    return new UserPreferences({
      ...this.props,
      onboarding_completed: completed,
      onboarding_tour_completed: tourCompleted,
      onboarding_skipped: skipped,
      onboarding_completed_at: completed ? new Date().toISOString() : this.props.onboarding_completed_at,
    });
  }

  /**
   * Create a new instance with updated tooltips seen
   */
  withTooltipSeen(tooltipId: string): UserPreferences {
    const tooltipsSeen = this.tooltipsSeen;
    if (tooltipsSeen.includes(tooltipId)) {
      return this;
    }
    return new UserPreferences({
      ...this.props,
      tooltips_seen: [...tooltipsSeen, tooltipId],
    });
  }

  /**
   * Create a new instance with updated activation checklist
   */
  withActivationChecklist(checklist: Partial<ActivationChecklist>): UserPreferences {
    return new UserPreferences({
      ...this.props,
      activation_checklist: { ...this.activationChecklist, ...checklist },
    });
  }

  /**
   * Create a new instance with updated LMS preferences
   */
  withLMSPreferences(lms: Partial<LMSPreferences>): UserPreferences {
    return new UserPreferences({
      ...this.props,
      lms: { ...this.lmsPreferences, ...lms },
    });
  }

  /**
   * Convert to a plain JSON object for storage
   */
  toJSON(): UserPreferencesProps {
    return { ...this.props };
  }

  /**
   * Check if two UserPreferences are equal
   */
  equals(other: UserPreferences): boolean {
    return (
      this.props.theme === other.props.theme &&
      this.props.onboarding_completed === other.props.onboarding_completed &&
      this.props.onboarding_tour_completed === other.props.onboarding_tour_completed &&
      JSON.stringify(this.props.tooltips_seen) === JSON.stringify(other.props.tooltips_seen) &&
      JSON.stringify(this.props.lms) === JSON.stringify(other.props.lms)
    );
  }
}
